/**
 * deriveMood(link, allLinks) → mood string.
 *
 * Rules (priority order):
 *   1. `sleep`      — link has 0 clicks today.
 *   2. `starstruck` — count ≥ target × 1.5 (user overshot big-time;
 *                     "star-struck" state the user explicitly asked
 *                     for — different from `overflow` which is a mild
 *                     overshoot).
 *   3. `overflow`   — count > effective target (over-achiever).
 *   4. `full`       — count === effective target (target just met).
 *   5. `winner`     — this card has the HIGHEST completion ratio among
 *                     links with at least 1 click, AND at least one
 *                     other link has non-zero progress (otherwise we're
 *                     the only mover and `charging` reads more honestly).
 *   6. `loser`      — this card has count > 0 AND the LOWEST ratio
 *                     while at least one sibling has moved further.
 *   7. `charging`   — count > 0, roughly on-pace (30–70 % of target).
 *   8. `neutral`    — everything else (has some progress but nothing
 *                     distinctive).
 *
 * Notes:
 *   * Comparisons use the EFFECTIVE daily target for each link
 *     (Feb-2026 random-targets feature) so tomorrow's re-roll doesn't
 *     make yesterday's winner look like today's loser.
 *   * Every branch returns a plain string so React memoisation in the
 *     consumer stays trivial. No object allocation.
 */
export function deriveMood(link, allLinks = []) {
  if (!link) return "neutral";
  const count = Math.max(0, Number(link.today_count) || 0);
  const target = Math.max(
    1,
    Number(link.effective_daily_target || link.daily_target) || 1,
  );
  if (count === 0) return "sleep";
  // Big overshoot — the "star-struck" tier the user asked for.
  // 1.5× target feels earned without being so rare it never fires.
  if (count >= target * 1.5) return "starstruck";
  if (count > target) return "overflow";
  if (count === target) return "full";

  // Comparative moods — only meaningful when there's context.
  const others = (allLinks || []).filter((l) => l && l.id !== link.id);
  if (others.length === 0) return "charging";

  const ratio = count / target;
  const moved = others.filter((l) => (Number(l.today_count) || 0) > 0);

  if (moved.length > 0) {
    const ratios = moved.map((l) => {
      const t = Math.max(
        1,
        Number(l.effective_daily_target || l.daily_target) || 1,
      );
      return (Number(l.today_count) || 0) / t;
    });
    const maxOther = Math.max(...ratios);
    const minOther = Math.min(...ratios);
    if (ratio >= maxOther && ratio > 0) return "winner";
    if (ratio <= minOther && maxOther > ratio) return "loser";
  }

  if (ratio >= 0.3 && ratio <= 0.7) return "charging";
  return "neutral";
}

/**
 * Six-tier reaction ladder — Feb-2026 user request:
 * "chillano" (চিল্লানো / screaming) escalation. One visibly different
 * emotion per +1 of the day so 6 taps aren't 6 identical bumps.
 *
 * Called with the count AFTER the +1 lands. ChargerIcon maps each
 * ladder key to an escalating base character + motion. Beyond 6 we
 * cap at the top tier (extra clicks stay in maximum-hype territory).
 */
export function getReactionLadder(newCount) {
  const n = Math.max(1, Number(newCount) || 1);
  if (n === 1) return "plus1";
  if (n === 2) return "plus2";
  if (n === 3) return "plus3";
  if (n === 4) return "plus4";
  if (n === 5) return "plus5";
  return "plus6";
}
